/**
 * Relation editor (`/relations/new`) — docs/08-frontend-and-ux.md §3.6.
 *
 * Reached from a profile with `?person=<id>`, which pre-fills one endpoint; the
 * labels name the direction explicitly ("parent" / "child") rather than leaning
 * on `person_a_id` / `person_b_id`, which mean nothing to a reader.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PersonSummary } from '@famtree/shared';
import { personsApi, relationsApi } from '../../api/client.js';
import { useSession } from '../../app/session.js';
import { problemMessage } from '../../ui/problem.js';
import { PersonPicker } from './PersonPicker.js';
import {
  RelationFields,
  emptyRelationDraft,
  toRelationPayload,
  type RelationDraft,
} from './RelationFields.js';
import { suggestedParentRole, toPersonSummary } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';
import { afterWrite } from '../proposals/afterWrite.js';

export function RelationEditorPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();

  const seedId = params.get('person') ?? '';
  const [draft, setDraft] = useState<RelationDraft>(() => emptyRelationDraft('parent_child'));
  const [personA, setPersonA] = useState<PersonSummary | null>(null);
  const [personB, setPersonB] = useState<PersonSummary | null>(null);
  const [summary, setSummary] = useState('');

  const seed = useQuery({
    queryKey: ['person', seedId],
    queryFn: () => personsApi.get(seedId),
    enabled: seedId !== '',
  });

  // The person we came from takes the first slot until the user moves them.
  useEffect(() => {
    if (!seed.data || personA || personB) return;
    const person = seed.data.person;
    setPersonA(toPersonSummary(person));
  }, [seed.data, personA, personB]);

  const create = useMutation({
    mutationFn: () =>
      relationsApi.create({
        relation: toRelationPayload(draft, personA?.id ?? null, personB?.id ?? null),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['person'] });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      navigate(
        afterWrite(
          result.proposal ? [result.proposal] : [],
          personA ? `/people/${personA.id}` : '/people',
        ),
      );
    },
  });

  const canWrite = user?.role === 'admin' || user?.can_propose;
  if (!canWrite) {
    return (
      <div className={ui.card}>
        <h1>{t('people.cannotProposeTitle')}</h1>
        <p>{t('people.cannotProposeBody')}</p>
        <Link to="/people">{t('people.backToList')}</Link>
      </div>
    );
  }

  const labels =
    draft.kind === 'parent_child'
      ? { a: t('relations.parent'), b: t('relations.child') }
      : draft.kind === 'union'
        ? { a: t('relations.partnerA'), b: t('relations.partnerB') }
        : { a: t('relations.siblingA'), b: t('relations.siblingB') };

  const ready = personA != null && personB != null && personA.id !== personB.id;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <h1>{t('relations.newTitle')}</h1>
      {create.isError && <p className={ui.problem}>{problemMessage(create.error, t)}</p>}

      <section className={ui.card}>
        <RelationFields draft={draft} onChange={setDraft} />

        <PersonPicker
          label={labels.a}
          value={personA}
          onChange={(person) => {
            setPersonA(person);
            if (person && draft.kind === 'parent_child') {
              setDraft((current) => ({
                ...current,
                parent_role: suggestedParentRole(person.gender),
              }));
            }
          }}
          excludeId={personB?.id}
        />
        <PersonPicker
          label={labels.b}
          value={personB}
          onChange={setPersonB}
          excludeId={personA?.id}
        />

        <label className={ui.field}>
          <span className={ui.label}>{t('people.fields.summary')}</span>
          <input
            className={ui.input}
            maxLength={200}
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
        </label>
      </section>

      <div className={styles.actions}>
        <button className={ui.button} type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? t('app.saving') : t('app.save')}
        </button>
        <Link className={ui.buttonSecondary} to={personA ? `/people/${personA.id}` : '/people'}>
          {t('app.cancel')}
        </Link>
      </div>
    </form>
  );
}
